/**
 * The names of the types OdysseyTales registers with Daedalus, in a module of their own so that code
 * naming a type (the frame suggestions) does not have to import the adapter that wires
 * those same modules together.
 */

/**
 * Type name of the entity every OdysseyTales map needs at least one of.
 */
export const SPAWN_POINT = "spawn_point";

/**
 * Type name of a shrine (ADR-096): a behaviour a placed obelisk carries. A character discovers it by stepping into
 * its ring and, once it has, stands up there after a death when it is the nearest it knows. It rests `available` or
 * `dormant` by each viewer's own page and takes no verb.
 */
export const SHRINE = "shrine";

/**
 * Type name of a waypoint (ADR-137 decision 2): a behaviour a placed portal stone carries. It is discovered as a shrine
 * is, by stepping into its cells, and is a place to travel to, never a respawn point. On the wire it is a shrine's kind
 * (`EntityKind.Shrine`), which gives the page its discovered and dormant looks and its pop.
 */
export const WAYPOINT = "waypoint";

/**
 * Type name of a sign: a behaviour a placed prop carries (ADR-053), with one state and a verb a player
 * aims with `E` (ADR-051). Any sign frame in the packs can be one.
 */
export const SIGN = "sign";

/**
 * Type name a placed area had while an area was an entity: a rectangle of tiles from its point, its top-left
 * corner, sized by its `w` and `h` fields and tagged by its `tags` field. Zones replaced it (ADR-054's amendment
 * of 2026-09-27); the name remains so `validate` refuses a world still holding one and `world:migrate` paints
 * each as zone cells.
 */
export const AREA = "area";

/**
 * Type name of the well: the first two-state behaviour (ADR-053), its bucket raised or lowered.
 */
export const WELL = "well";

/**
 * Type name of a gate (ADR-058): closed or open, moved by the opening and closing strips the pack draws.
 * Any gate art whose closed and open frames the author names can be one.
 */
export const GATE = "gate";

/**
 * Type name of a roof (ADR-058 amendment, P1.3-T4): closed, or open while the viewer's own character stands
 * under it, on that viewer's screen alone. A small interior built in place from the packs' separate interior,
 * wall, door and roof props takes it on its roof, which then rolls up by the strips the author names.
 */
export const ROOF = "roof";

/**
 * Type name of the training dummy (ADR-058's amendment for P2.2-T7): standing, or struck by a character's blow, which
 * the combat phase drives and no verb does. It takes no harm; its striker sees its numbers.
 */
export const DUMMY = "dummy";

/**
 * Type name of a chest (ADR-091 decision 17): closed or open, one picture for everyone, opened by a player and shut by
 * the map server. Any chest art whose closed and open frames the author names can be one; what it holds for a
 * character comes from the loot table it names.
 */
export const CHEST = "chest";

/**
 * Type name of a lantern (ADR-058 decisions 1 and 7): unlit or lit, one picture for everyone, lit and doused by a
 * player. Any lamp, lantern or torch art with an unlit and a lit frame can be one. It carries a power source and a
 * capacity as authored fields, which nothing reads yet.
 */
export const LANTERN = "lantern";

/**
 * Type name of the creature spawn (ADR-082 decision 3): a server-only marker naming a creature template, how many it
 * keeps, how long a dead one's slot waits and the painted area they spawn and wander in. The page never learns of it.
 */
export const CREATURE_SPAWN = "creature_spawn";

/**
 * Type name of an arrival point (ADR-122 decision 2.2): the spot a door or a room's host leads to, named per map
 * and facing the way a character arriving there faces.
 */
export const ARRIVAL_POINT = "arrival_point";

/**
 * Type name of an NPC (ADR-136 decision 2.1): a server-only marker naming an NPC template and a dialogue, how it rests
 * and moves and its temperament. The page never learns of it; it learns the NPC the map server spawns from it.
 */
export const NPC = "npc";

/**
 * Type name of a quest point (ADR-139 decision 3.4): a server-only marker of painted cells naming a place a quest's
 * `reach` objective names, with a label for the minimap arrow. The page never learns of it.
 */
export const QUEST_POINT = "quest_point";

/**
 * Type name of a field (ADR-144 decision 6): a server-only marker whose painted cells are farmland, either rental
 * patches its farm NPC lets out or a shared field. The page learns its cells from the version's public cell list.
 */
export const FIELD = "field";
